import React, { useState, useEffect } from 'react';
import { Box, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon, CheckCircle as CheckCircleIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import PageHeader from '../../../components/PageHeader/PageHeader';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const Publish = () => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    loadVersions();
  }, []);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const response = await SitesService.getVersions();
      setVersions(response || []);
    } catch (err) {
      console.error('Error loading versions:', err);
      SnackbarUtils.error(t('error_loading_versions') || 'Error al cargar las versiones');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePublishVersion = async () => {
    try {
      setPublishing(true);
      const response = await SitesService.publishConfig();
      
      SnackbarUtils.success(t('version_published_successfully') || 'Versión publicada exitosamente');
      setPublishDialogOpen(false);
      loadVersions();
    } catch (err) {
      console.error('Error publishing version:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_publishing_version') || 'Error al publicar la versión'));
    } finally {
      setPublishing(false);
    }
  };

  const handleActivateVersion = async (versionId) => {
    try {
      await SitesService.activateVersion(versionId);
      
      SnackbarUtils.success(t('version_activated_successfully') || 'Versión activada exitosamente');
      loadVersions();
    } catch (err) {
      console.error('Error activating version:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_activating_version') || 'Error al activar la versión'));
    }
  };

  const handlePreview = async () => {
    try {
      const response = await SitesService.getPreviewToken();
      const previewUrl = response.previewUrl || `https://sites.axium.com.uy/${response.token}`;
      
      // Abrir en una nueva pestaña (compatible con Electron)
      window.open(previewUrl, '_blank');
      
      SnackbarUtils.success(t('preview_opened') || 'Vista previa abierta en nueva pestaña');
    } catch (err) {
      console.error('Error getting preview token:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_opening_preview') || 'Error al abrir la vista previa'));
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatVersionNumber = (version) => {
    return version ? `v${version}` : '-';
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <PageHeader 
          title={t('publish') || 'Publicar'} 
          subtitle={t('publish_subtitle') || 'Gestiona las versiones de tu sitio web'} 
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={handlePreview}
          >
            {t('preview') || 'Vista Previa'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setPublishDialogOpen(true)}
          >
            {t('publish_version') || 'Publicar Versión'}
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : versions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('no_versions') || 'No hay versiones'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('no_versions_description') || 'Publica tu primera versión para comenzar'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('version') || 'Versión'}</TableCell>
                <TableCell>{t('status') || 'Estado'}</TableCell>
                <TableCell>{t('published_by') || 'Publicado por'}</TableCell>
                <TableCell>{t('published_at') || 'Fecha de Publicación'}</TableCell>
                <TableCell align="right">{t('actions') || 'Acciones'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {formatVersionNumber(version.version)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {version.isActive ? (
                      <Chip 
                        icon={<CheckCircleIcon />} 
                        label={t('active') || 'Activa'} 
                        color="success" 
                        size="small"
                      />
                    ) : (
                      <Chip 
                        label={t('inactive') || 'Inactiva'} 
                        color="default" 
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {version.publishedBy || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(version.publishedAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {!version.isActive && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleActivateVersion(version.id)}
                      >
                        {t('activate') || 'Activar'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para publicar versión */}
      <Dialog open={publishDialogOpen} onClose={() => setPublishDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('publish_version') || 'Publicar Versión'}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
            {t('publish_version_description') || '¿Está seguro de que desea publicar la configuración actual como una nueva versión?'}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('publish_version_info') || 'La configuración en edición (draft) se publicará como una nueva versión. La versión activa actual permanecerá disponible para activar nuevamente si es necesario.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)}>
            {t('cancel') || 'Cancelar'}
          </Button>
          <Button 
            onClick={handlePublishVersion} 
            variant="contained" 
            disabled={publishing}
          >
            {publishing ? (t('publishing') || 'Publicando...') : (t('publish') || 'Publicar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Publish;
